import { createGrpcAuthenticator } from 'infra/grpc-authenticator'
import { enableDevTools } from 'utils/grpc-util'
import memoize from 'memoizee'
import { JobProgressionServicePromiseClient } from '@taxfyle/api-internal/internal/job_progression_grpc_web_pb'
import { APIConfig } from 'types/definitions'
import {
  GetJobProgressionRequest,
  GetJobProgressionResponse,
  SetAuthorizationRequestedProgressionRequest,
  SetAuthorizationSignedProgressionRequest,
  SetDraftApprovedProgressionRequest,
  SetDraftInReviewProgressionRequest,
  SetDraftRejectedProgressionRequest,
  OverrideJobProgressionStepRequest,
  SetJobStartedProgressionRequest,
  SetOpenItemsProgressionRequest,
  SetReturnFiledProgressionRequest,
  SetDeliverableCompletedProgressionRequest,
  SetPreparationCompletedProgressionRequest,
  SetReviewCompletedProgressionRequest,
  SetExtensionInProgressProgressionRequest,
  SetExtensionConfirmedProgressionRequest,
  SetExtensionAcceptedProgressionRequest,
  SetExtensionRejectedProgressionRequest,
  SetReadyToFileProgressionRequest,
  GetAdditionalProgressionFlowsAvailableForJobRequest,
  GetAdditionalProgressionFlowsAvailableForJobResponse,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { filter } from 'rxjs/operators'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { Observable } from 'rxjs'
import TypeCase = RealtimeEvent.TypeCase

/**
 * Creates an instance of the job progression api.
 * @param config
 * @param events$
 * @returns
 */
export function createJobProgressionAPI(
  config: APIConfig,
  events$: Observable<RealtimeEvent>,
) {
  return new JobProgressionAPI(config, events$)
}

/**
 * Job Progression API.
 */
export class JobProgressionAPI {
  private readonly progressionClient = createJobProgressionClient(
    this.config.baseURL,
  )

  private readonly grpcAuthenticator = createGrpcAuthenticator(this.config)

  /**
   * Stream of events for job progression.
   */
  readonly events$: Observable<RealtimeEvent>

  constructor(
    private config: APIConfig,
    events$: Observable<RealtimeEvent>,
  ) {
    this.events$ = events$.pipe(
      filter((event) => event.getTypeCase() === TypeCase.JOB_PROGRESSION),
    )
  }

  /**
   * Gets all steps for a job progression.
   */
  async getProgression(
    request: GetJobProgressionRequest,
  ): Promise<GetJobProgressionResponse> {
    return this.progressionClient.getJobProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to open items.
   * @param request
   * @returns
   */
  async setOpenItemsProgression(request: SetOpenItemsProgressionRequest) {
    return this.progressionClient.setOpenItemsProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to job started.
   * @param request
   * @returns
   */
  async setJobStartedProgression(request: SetJobStartedProgressionRequest) {
    return this.progressionClient.setJobStartedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to draft in review.
   * @param request
   * @returns
   */
  async setDraftInReviewProgression(
    request: SetDraftInReviewProgressionRequest,
  ) {
    return this.progressionClient.setDraftInReviewProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to draft approved
   */
  async setDraftApprovedProgression(
    request: SetDraftApprovedProgressionRequest,
  ) {
    return this.progressionClient.setDraftApprovedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to draft rejected
   */
  async setDraftRejectedProgression(
    request: SetDraftRejectedProgressionRequest,
  ) {
    return this.progressionClient.setDraftRejectedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to authorization requested
   */
  async setAuthorizationRequestedProgression(
    request: SetAuthorizationRequestedProgressionRequest,
  ) {
    return this.progressionClient.setAuthorizationRequestedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to authorization signed
   */
  async setAuthorizationSignedProgression(
    request: SetAuthorizationSignedProgressionRequest,
  ) {
    return this.progressionClient.setAuthorizationSignedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to return filed
   */
  async setReturnFiledProgression(request: SetReturnFiledProgressionRequest) {
    return this.progressionClient.setReturnFiledProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to deliverable completed
   */
  async setDeliverableCompletedProgression(
    request: SetDeliverableCompletedProgressionRequest,
  ) {
    return this.progressionClient.setDeliverableCompletedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to specific step
   */
  async overrideJobProgressionStep(request: OverrideJobProgressionStepRequest) {
    return this.progressionClient.overrideJobProgressionStep(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  async setPreparationJobCompleted(
    request: SetPreparationCompletedProgressionRequest,
  ) {
    return this.progressionClient.setPreparationCompletedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to review completed step
   */
  async setReviewJobCompleted(request: SetReviewCompletedProgressionRequest) {
    return this.progressionClient.setReviewCompletedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to extension in progress.
   */
  async setExtensionInProgressProgression(
    request: SetExtensionInProgressProgressionRequest,
  ) {
    return this.progressionClient.setExtensionInProgressProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to extension confirmed.
   */
  async setExtensionConfirmedProgression(
    request: SetExtensionConfirmedProgressionRequest,
  ) {
    return this.progressionClient.setExtensionConfirmedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to extension accepted.
   */
  async setExtensionAcceptedProgression(
    request: SetExtensionAcceptedProgressionRequest,
  ) {
    return this.progressionClient.setExtensionAcceptedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to extension rejected.
   */
  async setExtensionRejectedProgression(
    request: SetExtensionRejectedProgressionRequest,
  ) {
    return this.progressionClient.setExtensionRejectedProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Sets the current progression to ready to file.
   */
  async setReadyToFileProgression(request: SetReadyToFileProgressionRequest) {
    return this.progressionClient.setReadyToFileProgression(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }

  /**
   * Gets all additional progression flows available for a job.
   */
  async getAdditionalProgressionFlowsAvailableForJob(
    request: GetAdditionalProgressionFlowsAvailableForJobRequest,
  ): Promise<GetAdditionalProgressionFlowsAvailableForJobResponse> {
    return this.progressionClient.getAdditionalProgressionFlowsAvailableForJob(
      request,
      await this.grpcAuthenticator.getMeta(),
    )
  }
}

/**
 * Creates a prompts client that is memoized.
 */
const createJobProgressionClient = memoize(function createJobProgressionClient(
  baseUrl: string,
) {
  const client = new JobProgressionServicePromiseClient(baseUrl)
  enableDevTools(client)
  return client
})
